import { ExperimentInfo, ExperimentsCatalogue } from '../types';
import { getPreferredExperimentAndCleanUpUrl, isBackendExperiment, isExperimentInConfig } from '../experiment-helper';
import { watch } from 'vue';
import { objectKeys } from '@/helpers/object-helper';
import { storeToRefs } from 'pinia';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';

import { useUserStore } from '@/stores/pinia/useUserStore';

export type NewExperimentsType = {
	name: string;
	countries: Array<string>;
	languages: Array<string>;
	sample: Array<number>;
	variant: string | null;
};

const isExperimentInitialized = false;
export function useExperimentInitialization() {
	const userStore = useUserStore();
	const { setAcquiredExperiments, setActivatedExperiments, setNewPreferredExperiments } = userStore;
	const { experimentsLoaded, activatedExperiments, _preferredExperiments } = storeToRefs(userStore);
	const { country, language } = storeToRefs(useLanguageStore());
	const { queryParams } = storeToRefs(useRoutingStore());

	function syncAvailableExperiments(newExperiments: NewExperimentsType[]) {
		const acquiredExperiments: ExperimentInfo[] = newExperiments
			/*
			 * - Countries: either no countries specified in the config or includes the current country
			 */
			.filter(el => el.countries?.length === 0 || el.countries?.includes(country.value))
			/*
			 * - Languages: either no languages specified in the config or includes the current language
			 */
			.filter(el => el.languages?.length === 0 || el.languages?.includes(language.value))
			.map(el => ({
				name: el.name as ExperimentInfo['name'],
				variant: el.variant as ExperimentInfo['variant'],
			}));

		const preferredExperiments = getPreferredExperimentAndCleanUpUrl(queryParams.value, acquiredExperiments);

		cleanAndPersistExperiments(acquiredExperiments, preferredExperiments);
	}

	function cleanAndPersistExperiments(
		initialAcquiredExperiments: ExperimentInfo[],
		preferredExperiments: ExperimentInfo[]
	) {
		try {
			/**
			 * Build a map of acquired experiments:
			 * - Only include backend experiments (prefixed with 'BE_') or those defined in `experimentConfig`
			 * - Format: { [experimentName]: variant }
			 */
			const acquiredExperimentsMap: ExperimentsCatalogue = initialAcquiredExperiments.reduce((acc, exp) => {
				if (isBackendExperiment(exp.name) || isExperimentInConfig(exp.name)) {
					acc[exp.name] = exp.variant;
				}

				return acc;
			}, {} as ExperimentsCatalogue);

			/**
			 * Merge existing preferred experiments with new ones from payload:
			 * - The array is transformed into an object directly
			 */
			const mergedPreferredExperiments = {
				...(_preferredExperiments.value as ExperimentsCatalogue),
				...preferredExperiments.reduce((acc, exp) => {
					acc[exp.name] = exp.variant;
					return acc;
				}, {} as ExperimentsCatalogue),
			};

			/**
			 * Validate preferred experiments:
			 * - Keep only if the experiment exists in config or is part of backend-acquired ones
			 */
			for (const name of objectKeys(mergedPreferredExperiments)) {
				const isBackendAvailable = isBackendExperiment(name) && acquiredExperimentsMap[name];

				if (!isExperimentInConfig(name) && !isBackendAvailable) {
					delete mergedPreferredExperiments[name];
				}
			}

			/**
			 * Filter activated experiments to only those present in the experiment config
			 */
			const validatedActivatedExperiments: string[] = (activatedExperiments.value ?? []).filter(entry => {
				const [experimentName] = entry.split(':');
				return isExperimentInConfig(experimentName);
			});

			/**
			 * Initialize the experiment store with validated data
			 */

			setAcquiredExperiments(acquiredExperimentsMap);
			setNewPreferredExperiments(mergedPreferredExperiments);
			setActivatedExperiments(validatedActivatedExperiments);
		} finally {
			if (experimentsLoaded.value === false) {
				experimentsLoaded.value = true;
			}
		}
	}

	function initializeExperiments(newExperiments: NewExperimentsType[]) {
		if (isExperimentInitialized) return;
		watch(
			() => [language.value, country.value],
			() => {
				syncAvailableExperiments(newExperiments);
			},
			{ immediate: true }
		);
	}

	return {
		initializeExperiments,
	};
}
